import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BreadCrumsHeader from "../../common/BreadCrums";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import Pagination from "../../common/TablePagination";
import "./invitation.scss";
// import CreateNewButton from "../../common/CreateNewButton";
import AddIcon from "../../../Assets/Icons/AddIcon.svg";
import { getMerchantInvites } from "../../../services/onboardingService";
import { useDispatch, useSelector } from "react-redux";
import { separateDateTimeAndConvertToLocale } from "../../../utils/utilFunctions";
import Loader from "../../common/Loader";

const InvitationListDataTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector((state) => state?.onboardingReducer?.loading);
  const [allInvites, setAllInvites] = useState([]);
  const [fullInvitesList, setFullInvitesList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);

  const updatePageSlice = (data, page) => {
    if (!data) return;
    const startIndex = (page - 1) * 50;
    const endIndex = startIndex + 50;
    setAllInvites(data.slice(startIndex, endIndex));
  };

  const getInvitesData = async () => {
    try {
      const invitesPromise = await dispatch(
        getMerchantInvites({ limit: 1000 }) // Fetch more for client-side filtering
      )?.unwrap();
      if (invitesPromise?.success === true) {
        // Filter to show only merchants who are in the invitation flow.
        // We show those with an explicit invitation marker (notifiedAt) 
        // or those in an early status (pending, accepted, etc.) while excluding fully processed accounts.
        const requestedData = invitesPromise?.data || [];
        const invitedMerchants = (requestedData || []).filter(
          (invite) => {
            const status = (invite?.status || "").toLowerCase();
            const hasCode = !!invite.code;

            // Show only records with a code (real invitations).
            // We show all invitations here regardless of status to maintain a complete history.
            return hasCode;
          }
        );

        // Sort by date descending (Latest on top)
        invitedMerchants.sort((a, b) => {
          const dateA = new Date(a.notifiedAt || a.createdAt || a.created_at || 0);
          const dateB = new Date(b.notifiedAt || b.createdAt || b.created_at || 0);
          return dateB - dateA;
        });

        setFullInvitesList(invitedMerchants);

        // Simple client-side pagination
        const total = Math.ceil((invitedMerchants?.length || 0) / 50);
        setTotalPages(total || 1);
        setCount(invitedMerchants?.length || 0);

        updatePageSlice(invitedMerchants, currentPage);
      }
    } catch (error) {
      console.error("Error in getInvitesData:", error);
    }
  };

  useEffect(() => {
    getInvitesData();
  }, []); // Initial load only

  useEffect(() => {
    if (fullInvitesList.length > 0) {
      updatePageSlice(fullInvitesList, currentPage);
    }
  }, [currentPage]);

  const items = [
    {
      title: "Merchants",
    },
    {
      title: "Invitation List",
    },
  ];

  const columns = [
    {
      name: "No.",
    },
    {
      name: "Date",
    },
    {
      name: "Email",
    },
    {
      name: "Code",
    },
    {
      name: "Status",
    },
  ];

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top of list when changing pages
    const container = document.getElementById('invitation-list-container');
    if (container) {
      container.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      {loading === true && <Loader />}
      <div className="flex justify-between col-span-12">
        <BreadCrumsHeader heading="Invitation List" items={items} />
        <div className="flex gap-3 items-center">


          <button
            onClick={() => navigate("/invitation-form")}
            className="relative sm:w-[150px] lg:w-[170px] h-[38px] rounded-[5px] bg-[#00ADEF] sm:text-[12px] lg:text-[14px] font-[600] font-nunito text-[#FFFFFF] text-center pl-[1.75rem] pr-[0.5rem] whitespace-nowrap flex items-center justify-center"
          >
            <img src={AddIcon} className="absolute left-[0.75rem] top-1/2 transform -translate-y-1/2 w-4 h-4" alt="Add" />
            <span className="ml-1">Invite New Merchant</span>
          </button>
        </div>
      </div>

      <div id="invitation-list-container" className="col-span-12 mt-8">
        <TableContainer className="w-[100%]">
          <Table
            sx={{ borderCollapse: "separate", borderSpacing: "0px 8px" }}
            aria-label="simple table"
          >
            <TableHead className="bg-[#00ADEF]">
              <TableRow
                className="h-[50px]"
                sx={{ boxShadow: "0px 4px 4px 0px #00000040" }}
              >
                {columns.map((column, index) => {
                  return <TableCell key={index} className="!text-[#FFFFFF]">{column.name}</TableCell>;
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {allInvites?.map((invite, index) => {
                return (
                  <TableRow
                    key={invite?._id || index}
                    className="h-[50px]"
                    sx={{
                      boxShadow: "0px 4px 4px 0px #00000040",
                      backgroundColor:
                        (index + 1) % 2 !== 0 ? "#F5F5F5" : "#FFFFFF",
                    }}
                  >
                    <TableCell>{(currentPage - 1) * 50 + index + 1}</TableCell>
                    <TableCell>
                      {
                        separateDateTimeAndConvertToLocale(invite?.createdAt || invite?.notifiedAt)
                          ?.localDate
                      }
                    </TableCell>
                    <TableCell>{invite?.email || invite?.user?.email || "N/A"}</TableCell>
                    <TableCell>{invite?.code || "N/A"}</TableCell>
                    <TableCell>
                      {(() => {
                        const status = invite?.status?.toLowerCase();
                        if (status === "pending" || !status) {
                          return (
                            <span className="px-3 py-1 rounded-full bg-[#E0F2FE] text-[#0369A1] border border-[#BAE6FD] text-[12px] font-[500] whitespace-nowrap">
                              Invited
                            </span>
                          );
                        }
                        if (status === "completed" || status === "accepted" || status === "approved") {
                          return (
                            <span className="px-3 py-1 rounded-full bg-[#DCFCE7] text-[#166534] border border-[#BBF7D0] text-[12px] font-[500] whitespace-nowrap">
                              Accepted
                            </span>
                          );
                        }
                        return (
                          <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 border border-gray-200 text-[12px] font-[500] whitespace-nowrap">
                            {invite?.status}
                          </span>
                        );
                      })()}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Pagination
          totalPages={totalPages}
          handlePageChange={handlePageChange}
        />
      </div>
    </>
  );
};

export default InvitationListDataTable;
